import React from 'react';
import moment from 'moment-timezone';
import { getWalkthroughScoreObject, nullSafeGet } from '../../utils/DataAccessUtils';
import useMountEffect from '../../hooks/useMountEffect';
import { parseScorePercentage } from '../../utils/WalkthroughHeatMapRangeUtils';
import {
	FetchHeatmapWalkthroughs,
	generateHeatmapMonths,
	getHeatmapRangeStart,
	HeatmapDataMap,
	HeatmapMonth,
	HeatmapSourceWalkthrough,
	HeatmapWalkthrough,
} from './heatmapUtils';

type GroupedByDate = Record<
	string,
	{
		walkthroughs: HeatmapWalkthrough[];
		totalPercentage: number;
		scoredCount: number;
	}
>;

export const transformWalkthroughsToHeatmapData = (
	walkthroughs: HeatmapSourceWalkthrough[],
	timeZoneId: string | undefined
): HeatmapDataMap => {
	const groupedByDate: GroupedByDate = walkthroughs
		.filter((wt) => wt.completedAt)
		.reduce((acc, wt) => {
			// Bucket by the same zone the cell popover renders in, so a
			// walkthrough completed near midnight lands in the day its
			// displayed timestamp shows (not the viewer's browser day).
			const date = (
				timeZoneId ? moment.tz(wt.completedAt, timeZoneId) : moment(wt.completedAt)
			).format('YYYY-MM-DD');
			// Reads the nested scores array when present and falls back to the flat overall* fields on
			// OpenSearch-backed walkthroughs (which no longer carry the scores array).
			const overallScore = getWalkthroughScoreObject(wt);

			// Cells are colored by percentageScore — the same number the popover shows — so the color
			// and the value can never disagree. A walkthrough counts as scored only when it has a max
			// possible score, which keeps a genuine 0% out of the "No score" bucket.
			const percentageScore = parseScorePercentage(nullSafeGet('percentageScore', overallScore));
			const maxPossibleScore = parseScorePercentage(nullSafeGet('maxPossibleScore', overallScore));
			const isScored = percentageScore !== null && maxPossibleScore !== null;

			if (!acc[date]) {
				acc[date] = {
					walkthroughs: [],
					totalPercentage: 0,
					scoredCount: 0,
				};
			}

			acc[date].walkthroughs.push({ ...wt, percentageScore: isScored ? percentageScore : null });
			if (isScored) {
				acc[date].totalPercentage += percentageScore;
				acc[date].scoredCount += 1;
			}

			return acc;
		}, {});

	// Build the data map with average score percentages
	const dataMap = Object.entries(groupedByDate).reduce((acc: HeatmapDataMap, [date, group]) => {
		acc[date] = {
			percentageScore: group.scoredCount > 0 ? group.totalPercentage / group.scoredCount : null,
			walkthroughs: group.walkthroughs,
		};

		return acc;
	}, {});

	return dataMap;
};

interface WalkthroughHeatmapData {
	loading: boolean;
	error: string | null;
	months: HeatmapMonth[];
}

// Fetches the site survey's walkthroughs for the last `monthCount` whole months (ending with the
// current one) and lays them out as calendar months. The inputs are read once, on mount: the page
// mounts the heatmap only after the site survey (and so its zone) has loaded. `targetCollectionName`
// must be unique per mounted heatmap — see fetchWalkthroughsForHeatmap.
export const useWalkthroughHeatmapData = (
	getWalkthroughData: FetchHeatmapWalkthroughs,
	siteSurveyId: string,
	timeZoneId: string | undefined,
	monthCount: number,
	targetCollectionName: string
): WalkthroughHeatmapData => {
	const [loading, setLoading] = React.useState(true);
	const [error, setError] = React.useState<string | null>(null);
	const [dataMap, setDataMap] = React.useState<HeatmapDataMap>({});

	// "Today" is the site's day, the same zone the walkthroughs are bucketed in, fixed for as long as
	// the page is open so the months drawn always match the window that was fetched.
	const [todayKey] = React.useState(() =>
		(timeZoneId ? moment.tz(timeZoneId) : moment()).format('YYYY-MM-DD')
	);

	useMountEffect(() => {
		// Both ends come from the site's day, not the browser's, and the date params carry no zone,
		// so each end is padded by a day: a walkthrough completed near midnight in a zone ahead of
		// (or behind) the backend's would otherwise fall outside the range. The end is the site's
		// today plus one day, then one more day of padding. Days outside the months are never drawn.
		const startDate = getHeatmapRangeStart(moment(todayKey), monthCount).subtract(1, 'day');
		const endDate = moment(todayKey).add(2, 'days');
		getWalkthroughData(siteSurveyId, startDate, endDate, targetCollectionName)
			.then((walkthroughs) =>
				setDataMap(transformWalkthroughsToHeatmapData(walkthroughs, timeZoneId))
			)
			.catch((fetchError) =>
				setError(nullSafeGet('message', fetchError) || 'Could not load walkthrough scores.')
			)
			.finally(() => setLoading(false));
	});

	const months = React.useMemo(
		() => generateHeatmapMonths(moment(todayKey), monthCount, dataMap),
		[todayKey, monthCount, dataMap]
	);

	return { loading, error, months };
};
